import React, { useMemo, useCallback } from 'react';
import type { FC } from 'react';
import { Box, useInput } from '@wix/cli-ui-kit';
import { lookup, match } from 'variant';
import { useTranslation } from '@wix/cli-i18n';
import {
  useOpenPage,
  useResolveDashboardExtensionDevUrl,
} from '../extensions/utils.js';
import type { DevCenterComponent } from '../../dev-center/components/dev-center-component.js';
import { CloseScreen } from '../CloseScreen/CloseScreen.js';
import { DashboardScreenSelector } from './DashboardScreen.js';
import type { DashboardExtension } from '../extensions/useExtensions.js';
import { useExtensions } from '../extensions/useExtensions.js';
import {
  NumericViewer,
  NumericOption,
} from '../NumericViewer/NumericViewer.js';
import { useWixCliAppBi } from '../../bi/index.js';
import { useExtensionData } from '../../context/extensionData.js';

const MAX_EXTENSIONS = 9;
const MAX_DASHBOARD_PAGES = 3;
const MAX_PAGE_PLUGINS = 3;
const MAX_MENU_PLUGINS = 3;

interface Props {
  onViewMore: (dashboardScreenSelector: DashboardScreenSelector) => void;
  onSelect?: (page: DevCenterComponent<DashboardExtension>) => void;
  onClose?: () => void;
}

export const DashboardNumericViewer: FC<Props> = ({
  onViewMore,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const { getExtensions } = useExtensions();
  const { getDashboardExtensionsRoute } = useExtensionData();
  const bi = useWixCliAppBi();
  const resolveDashboardExtensionUrl = useResolveDashboardExtensionDevUrl();
  const openPage = useOpenPage();

  const dashboardExtensions = useMemo(
    () => ({
      dashboardPages: getExtensions('DashboardPage'),
      dashboardMenuPlugins: getExtensions('DashboardMenuPlugin'),
      dashboardPlugins: getExtensions('DashboardPlugin'),
    }),
    [getExtensions]
  );

  const dashboardPagesData = dashboardExtensions.dashboardPages.slice(
    0,
    dashboardExtensions.dashboardPages.length > MAX_DASHBOARD_PAGES
      ? MAX_DASHBOARD_PAGES - 1
      : dashboardExtensions.dashboardPages.length
  );

  const dashboardPagesShowMoreIndex =
    dashboardExtensions.dashboardPages.length > dashboardPagesData.length
      ? dashboardPagesData.length + 1
      : undefined;

  const dashboardPagesCount = Math.max(
    dashboardPagesData.length,
    dashboardPagesShowMoreIndex ?? 0
  );

  const maxMenuPlugins = Math.min(
    MAX_MENU_PLUGINS,
    Math.floor((MAX_EXTENSIONS - dashboardPagesCount) / 2)
  );

  const dashboardMenuPluginsData =
    dashboardExtensions.dashboardMenuPlugins.slice(
      0,
      dashboardExtensions.dashboardMenuPlugins.length > maxMenuPlugins
        ? maxMenuPlugins - 1
        : dashboardExtensions.dashboardMenuPlugins.length
    );

  const dashboardMenuPluginsShowMoreIndex =
    dashboardExtensions.dashboardMenuPlugins.length >
    dashboardMenuPluginsData.length
      ? dashboardMenuPluginsData.length + dashboardPagesCount + 1
      : undefined;

  const dashboardMenuPluginsCount = Math.max(
    dashboardMenuPluginsData.length + dashboardPagesCount,
    dashboardMenuPluginsShowMoreIndex ?? 0
  );

  const maxPagePlugins = Math.min(
    MAX_PAGE_PLUGINS,
    MAX_EXTENSIONS - dashboardMenuPluginsCount
  );

  const dashboardPluginsData = dashboardExtensions.dashboardPlugins.slice(
    0,
    dashboardExtensions.dashboardPlugins.length > maxPagePlugins
      ? maxPagePlugins - 1
      : dashboardExtensions.dashboardPlugins.length
  );

  const dashboardPluginsShowMoreIndex =
    dashboardExtensions.dashboardPlugins.length > dashboardPluginsData.length
      ? dashboardPluginsData.length + dashboardMenuPluginsCount + 1
      : undefined;

  const visibleItems: Array<
    NumericOption<DevCenterComponent<DashboardExtension>, DashboardExtension>
  > = [
    ...dashboardPagesData.map((page) =>
      NumericOption.Item({
        title: page.title,
        description: page.route,
        value: page,
      })
    ),
    ...(dashboardPagesShowMoreIndex
      ? [NumericOption.ViewMore('DashboardPage' as DashboardExtension)]
      : []),
    ...dashboardMenuPluginsData.map((menuPlugin) =>
      NumericOption.Item({
        title: menuPlugin.title,
        description: getDashboardExtensionsRoute(menuPlugin),
        value: menuPlugin,
      })
    ),
    ...(dashboardMenuPluginsShowMoreIndex
      ? [NumericOption.ViewMore('DashboardMenuPlugin' as DashboardExtension)]
      : []),
    ...dashboardPluginsData.map((pagePlugin) =>
      NumericOption.Item({
        title: pagePlugin.title,
        description: getDashboardExtensionsRoute(pagePlugin),
        value: pagePlugin,
      })
    ),
    ...(dashboardPluginsShowMoreIndex
      ? [NumericOption.ViewMore('DashboardPlugin' as DashboardExtension)]
      : []),
  ];

  const getLabelKey = (
    dashboardExtension: DevCenterComponent<DashboardExtension>
  ) => {
    return match(
      dashboardExtension,
      lookup({
        DashboardPage: 'general.dashboard_pages_viewer.title',
        DashboardMenuPlugin: 'general.dashboard_menu_plugins_viewer.title',
        DashboardPlugin: 'general.dashboard_plugins_viewer.title',
        DashboardModal: '', // NOTE: No key for this type
      })
    );
  };

  useInput(async (input) => {
    const inputNumber = Number(input);
    if (inputNumber > 0 && inputNumber <= visibleItems.length) {
      const itemIndex = inputNumber - 1;
      const item = visibleItems.at(itemIndex);
      if (item) {
        await match(item, {
          Item: async (item) => {
            bi.cliAction({
              question: t(getLabelKey(item.value)),
              questionKey: getLabelKey(item.value),
              action: item.title,
              key: input,
            });
            await handleSelect(item.value);
          },
          ViewMore: (viewMore) => {
            bi.cliAction({
              question: t('general.numeric_viewer.show_more'),
              questionKey: 'general.numeric_viewer.show_more',
              action: 'view_more',
              key: input,
            });
            onViewMore(
              DashboardScreenSelector.Arrow({
                dashboardExtension: viewMore.payload,
              })
            );
          },
        });
      }
    }
  });

  const handleSelect = useCallback(
    async (dashboardExtension: DevCenterComponent<DashboardExtension>) => {
      const url = resolveDashboardExtensionUrl(dashboardExtension);

      await openPage({
        url,
        title: dashboardExtension.title,
      });
      onSelect?.(dashboardExtension);
    },
    [onSelect, openPage, resolveDashboardExtensionUrl]
  );

  const dashboardPages = visibleItems.slice(0, dashboardPagesCount);
  const dashboardMenuPlugins = visibleItems.slice(
    dashboardPagesCount,
    dashboardMenuPluginsCount
  );
  const dashboardPlugins = visibleItems.slice(dashboardMenuPluginsCount);

  return (
    <Box flexDirection="column" gap={1}>
      {dashboardPages.length > 0 && (
        <NumericViewer
          startIndex={0}
          titleKey="general.dashboard_pages_viewer.title"
          items={dashboardPages}
        />
      )}
      {dashboardMenuPlugins.length > 0 && (
        <NumericViewer
          startIndex={dashboardPagesCount}
          titleKey="general.dashboard_menu_plugins_viewer.title"
          items={dashboardMenuPlugins}
        />
      )}
      {dashboardPlugins.length > 0 && (
        <NumericViewer
          startIndex={dashboardMenuPluginsCount}
          titleKey="general.dashboard_plugins_viewer.title"
          items={dashboardPlugins}
        />
      )}
      {onClose && <CloseScreen onClose={onClose} />}
    </Box>
  );
};
